import React, { useEffect, useMemo, useState } from 'react';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectMarketPricesBySymbol } from '../../../../Redux/Slices/MarketPricesSlice';
import useCurrencyConversionApi from '../../../Apis/Hooks/useCurrencyConversionApiHook';
import useSymbolsApi from '../../../Apis/Hooks/useSymbolsApiHook';
import useUnitOfMeasureConversionApi from '../../../Apis/Hooks/useUnitOfMeasureConversionApiHook';
import CardSkeleton from '../../../Components/Layout/CardSkeleton';
import { IExchangeCommoditySelection } from '../../../Components/Navigation/Hooks/useExchangeCommodityNavigationHook';
import formattingService from '../../../Services/Formatting/FormattingService';
import { FuturesPricesChartProps } from '../../Futures/Prices/FuturesPricesChart';
import { SymbolContractCompositeModel } from '../../Futures/Prices/FuturesPricesTableDefinitions';
import { IWithPageElementProps } from '../DashboardCustomizationDefinitions';

const withFuturesPricesChartFromPageElement = (FuturesPricesChart: React.ComponentType<FuturesPricesChartProps>): React.FC<IWithPageElementProps> => {
    const Wrapped: React.FC<IWithPageElementProps> = (props) => {
        if (!props.pageElement) {
            return <> </>;
        }

        const { demeterPageElementGuid, commodity, exchange, currency, unitOfMeasure } = props.pageElement;
        const marketPricesBySymbol = useApplicationSelector(selectMarketPricesBySymbol);
        const [symbolContractSelections, setSymbolContractSelections] = useState<SymbolContractCompositeModel[]>([]);
        const [selectedContractIndexes, setSelectedContractIndexes] = useState<number[]>([]);

        const symbols = useSymbolsApi();

        const symbolModel = useMemo(() => {
            if (!symbols || !exchange || !commodity) {
                return undefined;
            }
            return symbols.find((x) => x.exchange === exchange && x.commodity === commodity);
        }, [symbols, exchange, commodity]);

        const [getUnitOfMeasureConversionRate] = useUnitOfMeasureConversionApi(symbolModel?.reutersInstrumentCodePrefix);
        const [getCurrencyConversionRate] = useCurrencyConversionApi(symbolModel?.reutersInstrumentCodePrefix);

        useEffect(() => {
            if (!symbolModel?.symbolContracts) {
                return;
            }

            if (symbolModel.symbolContracts) {
                const contracts = symbolModel.symbolContracts.slice(0, 10).map((row, index) => {
                    const contract: SymbolContractCompositeModel = {
                        id: `${index}`,
                        rowIndex: index,
                        selected: true,
                        multipler: symbolModel.priceMultiplierFutures,
                        reutersInstrumentCodePrefix: symbolModel.reutersInstrumentCodePrefix,
                        ...row,
                    };

                    const marketPrice = marketPricesBySymbol[row.reutersInstrumentCode];

                    if (!marketPrice) {
                        return contract;
                    }

                    return convertMarketPrice({
                        ...contract,
                        ...marketPrice,
                    });
                });

                setSymbolContractSelections(contracts);
            }
        }, [symbolModel]);

        useEffect(() => {
            if (!symbolContractSelections.length) {
                return;
            }

            const selectedContractsBuffers = props.selectedContracts ? props.selectedContracts.split(',').map((x) => parseInt(x, 10)) : [1];

            const selectedContractDates = selectedContractsBuffers.map((contractBuffer) => {
                const currentDate = new Date();
                const contractDate = new Date(currentDate.setMonth(currentDate.getMonth() + contractBuffer));
                return contractDate;
            });

            symbolContractSelections.forEach((row) => {
                if (selectedContractDates.some((date) => date.getMonth() === row.month - 1 && date.getFullYear() === row.year)) {
                    setSelectedContractIndexes((previous) => [...previous, row.rowIndex]);
                }
            });
        }, [symbolContractSelections]);

        const convertMarketPrice = (contract: SymbolContractCompositeModel): SymbolContractCompositeModel => {
            const convert = (value: number | undefined) => {
                if (value === undefined) {
                    return undefined;
                }
                return getUnitOfMeasureConversionRate(unitOfMeasure) * getCurrencyConversionRate(currency, contract.year, contract.month) * value;
            };

            return {
                ...contract,
                ...{
                    contractYearMonth: `${contract.year}${contract.month}`,
                    latestPrice: contract.latestPrice ?? undefined,
                    latestPriceConverted: convert(contract.latestPrice),
                    latestOrSettledPriceConverted: convert(contract.latestPrice) ?? convert(contract.settlementPrice),
                    bidPriceConverted: convert(contract.bidPrice),
                    askPriceConverted: convert(contract.askPrice),
                    settlementPriceConverted: convert(contract.settlementPrice),
                    netChangeConverted: convert(contract.netChange),
                    openPriceConverted: convert(contract.openPrice),
                    expirationDateFormatted: formattingService.toShortDayMonthYear(contract.expirationDate),
                    lastSessionHighPriceConverted: convert(contract.lastSessionHighPrice),
                    lastSessionLowPriceConverted: convert(contract.lastSessionLowPrice),
                    settlementNetChangeConverted: convert(contract.settlementNetChange),
                    highPriceConverted: convert(contract.highPrice),
                    lowPriceConverted: convert(contract.lowPrice),
                    tradeRegistrationPriceConverted: convert(contract.tradeRegistrationPrice),
                    currency: currency ?? contract.currency,
                },
            };
        };

        const exchangeCommoditySelection = {
            exchange,
            commodity,
        } as IExchangeCommoditySelection;

        if (!symbolContractSelections) {
            return <CardSkeleton />;
        }

        const chartProps: FuturesPricesChartProps = {
            exchangeCommoditySelection,
            customTitle: props.customTitle,
            symbolContracts: symbolContractSelections,
            currency: currency ?? symbolModel?.currency,
            unitOfMeasure: unitOfMeasure ?? symbolModel?.unitOfMeasure,
            testId: `DashboardFuturesPricesChart-${demeterPageElementGuid}`,
            dragIndicatorIcon: props.dragIndicatorIcon,
            isInDashboardEditMode: props.isInDashboardEditMode,
            maximizeMinimizeButton: props.maximizeMinimizeButton,
            maxWidthHeightRatio: props.maxWidthHeightRatio,
            handleMoreOptionsSelection: props.handleMoreOptionsSelection,
            visibleIndexes: selectedContractIndexes.length ? selectedContractIndexes : [0],
            track: props.track,
        };

        return <FuturesPricesChart {...chartProps} />;
    };

    return Wrapped;
};

export default withFuturesPricesChartFromPageElement;
